import React, { useEffect, useRef, useState } from "react";

import { useMutation } from "@apollo/client";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";

import { ACCOUNT_MUTATIONS } from "components/graphql";
import { useTranslation } from "components/i18n/source";
import { Button, Form, Input, Section, Text } from "components/ui/source";

import { validateResetPasswordForm } from "./validator";

import styles from "./Accounts.module.scss";

function ResetPasswordForm({ setLoading, onRequestBack, onRequestClose }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const emailFormRef = useRef(null);

  const [startSubmit, { data, loading, error }] = useMutation(
    ACCOUNT_MUTATIONS.SEND_RESET_PASSWORD_INSTRUCTIONS_EMAIL,
  );

  const prefix = "modals.accounts.resetPassword";

  // Provide current loading state
  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  // Start graphQL request
  function onSubmitEmail(account) {
    setEmail(account.email);
    startSubmit({
      variables: {
        email: account.email,
      },
    });
  }

  return (
    <>
      <Section
        className={classnames(
          styles.reset_password_container,
          data && styles.hide,
        )}
      >
        <Text
          className={classnames(styles.header, styles.centered_text)}
          types={["h4", "medium"]}
          translateKey={`${prefix}.title`}
        />

        <Text
          className={styles.centered_text}
          types={["subtitles"]}
          translateKey={`${prefix}.description`}
        />

        <Text
          className={classnames(
            styles.error_message,
            styles.centered_text,
            styles.error,
            error && styles.visible,
          )}
          types={["subtitles", "accent-red"]}
          translateKey={`${prefix}.error`}
        />

        <Form
          className={styles.form_container}
          validate={values => validateResetPasswordForm(values, t)}
          initialValues={{}}
          initialErrors={{ email: "" }}
          onSubmit={onSubmitEmail}
          innerRef={emailFormRef}
          validateOnChange={true}
        >
          {({ handleSubmit, errors }) => (
            <>
              <Input
                placeholder={t(`${prefix}.cta`)}
                name="email"
                theme="popup"
                required
              />

              <Button
                textProps={{
                  translateKey: `${prefix}.cta`,
                  types: ["subtitles"],
                }}
                upperCase={false}
                onClick={handleSubmit}
                disabled={!isEmpty(errors)}
              />
            </>
          )}
        </Form>

        {onRequestBack && (
          <Section
            className={classnames(styles.centered_text, styles.back_button)}
          >
            <Button
              type="no_background"
              textProps={{
                translateKey: `${prefix}.back`,
                types: ["subtitles", "underline"],
              }}
              upperCase={false}
              onClick={onRequestBack}
            />
          </Section>
        )}
      </Section>

      <Section
        className={classnames(
          styles.reset_password_container,
          !data && styles.hide,
        )}
      >
        <Text
          className={classnames(styles.header, styles.centered_text)}
          types={["h4", "medium"]}
          translateKey={`${prefix}.successTitle`}
        />

        <Text
          className={classnames(styles.centered_text, styles.success)}
          types={["subtitles"]}
          translateKey={`${prefix}.successDescription`}
          interpolation={{ email: email }}
        />

        <Button
          textProps={{
            translateKey: `${prefix}.close`,
            types: ["subtitles"],
          }}
          upperCase={false}
          onClick={onRequestClose}
        />
      </Section>
    </>
  );
}

ResetPasswordForm.propTypes = {
  setLoading: PropTypes.func,
  onRequestBack: PropTypes.func,
  onRequestClose: PropTypes.func,
};

ResetPasswordForm.defaultProps = {
  setLoading: () => {},
  onRequestBack: null,
  onRequestClose: () => {},
};

export default ResetPasswordForm;
